import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CalendarCheck, X, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDividendDatesButton — Round 88 (Feb 2026).
 *
 * Single admin-only button that owns the "make every ticker's ex-div /
 * pay date 100% Massive-accurate" workflow. Two server actions chained
 * in one click:
 *
 *   Phase 1 — Backfill: POST /api/admin/massive/backfill-now
 *     Pulls dividend history from Massive for every ticker NOT yet in
 *     `dividend_history_massive`. Trial plan: ~9 hr. Paid plan: ~10 min.
 *
 *   Phase 2 — Heal:     POST /api/admin/massive/heal-stock-cache
 *     Walks every backfilled ticker and overwrites the stock_cache
 *     `last_payment_date / next_ex_date / next_payment_date / ...`
 *     fields with Massive's verbatim values, then stamps
 *     `dividend_authoritative_source=massive` so Yahoo enrichments
 *     can never clobber them again. ~1-2 min for ~4,600 tickers.
 *
 * UI: stats pill on the left shows live "Locked X / Y (Z%)" coverage,
 * tone shifts rose < 60% → amber < 95% → emerald ≥ 95%. Click the
 * button → confirm modal → fires backfill (non-blocking), then heals.
 * Polls the stats endpoint every 5s during a run.
 */
export default function AdminDividendDatesButton() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(null); // null | 'backfilling' | 'healing' | 'done' | 'error'
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lastHealResult, setLastHealResult] = useState(null);
  const pollRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/massive/dividend-dates-stats`,
        { withCredentials: true },
      );
      setStats(data);
    } catch {
      /* non-admin → 403 → button stays hidden */
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    fetchStats();
    const inFlight = phase === 'backfilling' || phase === 'healing';
    const id = setInterval(fetchStats, inFlight ? 5000 : 120000);
    pollRef.current = id;
    return () => clearInterval(id);
  }, [user, fetchStats, phase]);

  if (user?.role !== 'admin' || !stats) return null;

  const { total_payers, backfilled_tickers, locked, missing_backfill, missing_lock } = stats;
  const pct = total_payers ? (locked / total_payers) * 100 : 0;
  const tone =
    pct >= 95
      ? 'text-emerald-600 dark:text-emerald-400'
      : pct >= 60
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-rose-600 dark:text-rose-400';

  const fire = async () => {
    if (busy) return;
    setBusy(true);
    setLastError(null);
    setLastHealResult(null);
    try {
      // Phase 1 — kick off the backfill. Runs in the background on the
      // server (returns 200 immediately). We just wait a generous
      // window for Massive's paid plan to chew through ~2,600 tickers.
      setPhase('backfilling');
      await axios.post(
        `${API}/admin/massive/backfill-now`,
        {},
        { withCredentials: true },
      );
      toast.success('Backfill started', {
        description: 'Pulling Massive dividend history for missing tickers. Heal will fire when it finishes.',
      });

      // Wait for the backfill server-side task to finish. We don't
      // have a status endpoint for the runner itself, so we poll the
      // stats endpoint — backfilled_tickers count converging on
      // total_payers is our signal.
      const startBackfilled = backfilled_tickers;
      const targetBackfilled = total_payers;
      const waitForBackfill = async () => {
        for (let i = 0; i < 240; i += 1) { // up to ~20 min @ 5s
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const { data } = await axios.get(
              `${API}/admin/massive/dividend-dates-stats`,
              { withCredentials: true },
            );
            setStats(data);
            const stalled = i > 12 && data.backfilled_tickers === startBackfilled;
            const done = data.backfilled_tickers >= targetBackfilled - 30; // 30-ticker tolerance for tickers Massive can't cover
            if (done || stalled) return data;
          } catch {
            /* swallow and keep polling */
          }
        }
        return null;
      };
      await waitForBackfill();

      // Phase 2 — heal. Now runs in the background server-side
      // (Round 92o++++++++N++++++++++, Jun 2026) to avoid ingress 504s
      // on full ~4,600-ticker sweeps. Fire the POST, then poll the
      // status endpoint until `in_progress=false`.
      setPhase('healing');
      await axios.post(
        `${API}/admin/massive/heal-stock-cache`,
        {},
        { withCredentials: true, timeout: 30000 },
      );

      const waitForHeal = async () => {
        // Poll every 3s for up to 30 min — that's longer than the
        // worst-case full sweep on paid throttle (~5 min).
        for (let i = 0; i < 600; i += 1) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const { data } = await axios.get(
              `${API}/admin/massive/heal-stock-cache-status`,
              { withCredentials: true },
            );
            // Surface live progress to the modal so the user sees
            // "Patched 1247 / 4620" tick up instead of a blank spinner.
            setLastHealResult({
              patched: data.patched,
              tickers_scanned: data.tickers_scanned,
              skipped_no_match: data.skipped_no_match,
              failed: data.failed,
              in_progress: data.in_progress,
            });
            if (!data.in_progress && data.finished_at) return data;
          } catch {
            /* swallow and keep polling */
          }
        }
        return null;
      };
      const healResult = await waitForHeal();
      if (healResult) {
        setLastHealResult(healResult);
        toast.success('Dividend dates locked', {
          description: `${healResult.patched?.toLocaleString?.()} tickers patched · ${healResult.tickers_scanned?.toLocaleString?.()} scanned.`,
          duration: 10000,
        });
      } else {
        toast.warning('Heal started but did not finish within 30 min', {
          description: 'Check /admin/massive/heal-stock-cache-status manually.',
        });
      }
      setPhase('done');
      await fetchStats();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      setLastError(msg);
      setPhase('error');
      toast.error('Backfill + Lock failed', { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const inProgress = phase === 'backfilling' || phase === 'healing';

  return (
    <>
      {/* Coverage pill */}
      <div
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20"
        title="Tickers whose ex-div + pay dates are sourced from Massive and locked against Yahoo overwrites."
        data-testid="admin-div-dates-coverage-pill"
      >
        <Lock className={`w-3 h-3 ${tone}`} />
        Dates locked:{' '}
        <span className={`font-bold font-mono ${tone}`} data-testid="admin-div-dates-locked-count">
          {Number(locked).toLocaleString()}
        </span>
        <span className="text-muted-foreground/60">
          / {Number(total_payers).toLocaleString()}
        </span>
        <span className={`ml-1 font-mono ${tone}`}>({pct.toFixed(1)}%)</span>
      </div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: pull Massive dividend history for un-backfilled tickers, then lock every stock_cache row's pay dates so Yahoo can't overwrite them."
        data-testid="admin-div-dates-fire"
      >
        {inProgress ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarCheck className="w-3 h-3" />}
        {phase === 'backfilling'
          ? 'Backfilling…'
          : phase === 'healing'
            ? 'Locking dates…'
            : 'Backfill + Lock Dividend Dates'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="admin-div-dates-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-xl shadow-2xl w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border/60">
              <div className="flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" />
                <h2 className="text-sm font-bold">Backfill + Lock Dividend Dates</h2>
              </div>
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                disabled={busy}
                className="text-muted-foreground hover:text-foreground p-1 rounded disabled:opacity-40"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="rounded-md border p-3 space-y-1.5 text-[11px]">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dividend-paying tickers</span>
                  <span className="font-mono font-bold">{total_payers.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Backfilled from Massive</span>
                  <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    {backfilled_tickers.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Locked (Yahoo-proof)</span>
                  <span className={`font-mono font-bold ${tone}`}>{locked.toLocaleString()}</span>
                </div>
                <div className="flex justify-between pt-1.5 border-t border-border/40">
                  <span className="text-muted-foreground">Missing backfill</span>
                  <span className="font-mono font-bold text-rose-600 dark:text-rose-400">
                    {missing_backfill.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Missing lock</span>
                  <span className="font-mono font-bold text-rose-600 dark:text-rose-400">
                    {missing_lock.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="text-[11px] text-muted-foreground space-y-1.5 leading-relaxed">
                <p>This runs two server actions in sequence:</p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>
                    <strong className="text-foreground">Backfill</strong> — pulls Massive dividend history for every un-backfilled
                    dividend-paying ticker (~10 min on the paid plan, ~9 hr on trial).
                  </li>
                  <li>
                    <strong className="text-foreground">Lock</strong> — overwrites stock_cache pay dates with Massive&apos;s verbatim values
                    and stamps every row so Yahoo can never overwrite them again (~1-2 min).
                  </li>
                </ol>
                <p className="pt-1 text-[10px]">
                  Idempotent — safe to re-run any time. Use this after each deploy to production.
                </p>
              </div>

              {phase === 'backfilling' && (
                <div className="rounded-md border border-amber-300/60 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-700/60 px-3 py-2 text-[11px] flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600 dark:text-amber-400" />
                  Backfill running on the server. Coverage will tick up live. Lock fires automatically when done.
                </div>
              )}
              {phase === 'healing' && (
                <div className="rounded-md border border-sky-300/60 bg-sky-50/70 dark:bg-sky-950/20 dark:border-sky-700/60 px-3 py-2 text-[11px] flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600 dark:text-sky-400" />
                  Locking stock_cache rows…
                </div>
              )}
              {phase === 'done' && lastHealResult && (
                <div className="rounded-md border border-emerald-300/60 bg-emerald-50/70 dark:bg-emerald-950/20 dark:border-emerald-700/60 px-3 py-2 text-[11px]">
                  <div className="flex items-center gap-1.5 font-bold text-emerald-700 dark:text-emerald-300 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Run complete
                  </div>
                  <div className="grid grid-cols-2 gap-1 font-mono">
                    <span>Patched: <b>{lastHealResult.patched?.toLocaleString?.()}</b></span>
                    <span>Scanned: <b>{lastHealResult.tickers_scanned?.toLocaleString?.()}</b></span>
                    <span>No match: <b>{lastHealResult.skipped_no_match?.toLocaleString?.()}</b></span>
                    <span>Failed: <b>{lastHealResult.failed?.toLocaleString?.()}</b></span>
                  </div>
                </div>
              )}
              {phase === 'error' && lastError && (
                <div className="rounded-md border border-rose-300/60 bg-rose-50/70 dark:bg-rose-950/20 dark:border-rose-700/60 px-3 py-2 text-[11px] flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-rose-700 dark:text-rose-300">Run aborted</div>
                    <div className="text-muted-foreground break-words mt-0.5">{lastError}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 pb-4 pt-1 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted disabled:opacity-50"
                data-testid="admin-div-dates-cancel"
              >
                Close
              </button>
              <button
                type="button"
                onClick={fire}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:opacity-60"
                data-testid="admin-div-dates-confirm"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarCheck className="w-3 h-3" />}
                {busy ? (phase === 'healing' ? 'Locking…' : 'Backfilling…') : 'Run now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
